import { t } from "@lingui/macro";
import { SharedValue } from "react-native-reanimated";

import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { SignedOutBanner } from "@whoosh/design-system/src/apps/PatronApp/layout/SignedOutBanner";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  Stack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { useFlags } from "../../../../utils/launchDarkly";
import { TimeSlotList } from "../TimeSlotList";

type TeeSheetAgendaProps = {
  selectedCourseIds: string[] | null;
  facilityId: string;
  handleRowPress: (slotId: string) => void;
  handleRequestedRowPress: (queueSlotId: string, time: string) => void;
  onJoinableRowPress: (slotId: string) => void;
  currentSlotId?: string;
  memberId: string;
  membershipTypeId: string;
  clientClass: "MEMBER" | "CUSTOMER";
  setIsScrolled: (isScrolled: boolean) => void;
  scrollY?: SharedValue<number>;
  shouldShowFooter: boolean;
  selectedText: string;
  maximumText: string;
  onReserveMultipleTimes: () => void;
  isSignedIn?: boolean;
  isMobileScreen?: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  onLoginAction?: () => void;
};

export const TeeSheetAgenda = ({
  selectedCourseIds,
  facilityId,
  handleRowPress,
  handleRequestedRowPress,
  onJoinableRowPress,
  currentSlotId,
  memberId,
  membershipTypeId,
  clientClass,
  setIsScrolled,
  scrollY,
  shouldShowFooter,
  selectedText,
  maximumText,
  onReserveMultipleTimes,
  isSignedIn,
  isMobileScreen,
  title,
  subtitle,
  buttonText,
  onLoginAction,
}: TeeSheetAgendaProps) => {
  const { newTopbar } = useFlags();
  return (
    <>
      <VStack
        flex={1}
        style={{
          position: "relative",
          paddingBottom: shouldShowFooter ? 80 : 0,
        }}
      >
        {selectedCourseIds ? (
          <TimeSlotList
            facilityId={facilityId}
            handleRowPress={handleRowPress}
            onJoinableRowPress={onJoinableRowPress}
            handleRequestedRowPress={handleRequestedRowPress}
            currentSlotId={currentSlotId}
            memberID={memberId}
            selectedCourseIds={selectedCourseIds}
            setIsScrolled={setIsScrolled}
            scrollY={scrollY}
            membershipTypeId={membershipTypeId}
            clientClass={clientClass}
          />
        ) : null}
        {!newTopbar && !isSignedIn && !isMobileScreen ? (
          <SignedOutBanner
            position="top"
            isOpen={!isSignedIn}
            title={title ?? ""}
            subTitle={subtitle ?? ""}
            actionButton={
              <Button
                variant="white"
                size="tiny"
                onPress={onLoginAction}
                accessibilityLabel={(buttonText ?? "") + t` button`}
              >
                <Text variant="mainTiny" color="text-primary">
                  {buttonText ?? ""}
                </Text>
              </Button>
            }
          />
        ) : null}
      </VStack>
      {shouldShowFooter ? (
        <ScreenFooter show={shouldShowFooter}>
          <Stack
            alignItems="center"
            gap={2}
            width="100%"
            flexDirection={{ base: "row", md: "column" }}
          >
            <Stack
              gap={1}
              width={{ base: 120, md: "auto" }}
              flexDirection={{ base: "column", md: "row" }}
            >
              <Text variant="mainSmallSemiBold" pr={1}>
                {selectedText}
              </Text>
              <Text variant="mainSmallSemiBold" color="text-secondary">
                {maximumText}
              </Text>
            </Stack>
            <Box flex={1} width={{ base: "auto", md: "100%" }}>
              <Button
                testID="reserve-multiple-times-button"
                onPress={onReserveMultipleTimes}
                accessibilityLabel={t`Reserve Times button`}
              >{t`Reserve Times`}</Button>
            </Box>
          </Stack>
        </ScreenFooter>
      ) : null}
    </>
  );
};
